import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Canvas, Image as FabricImage } from 'fabric'
import type { Object as FabricObject, TPointerEventInfo, TPointerEvent } from 'fabric'

interface FabricCanvasProps {
  sprites: Array<{
    id: string
    imageUrl: string
    position?: { x: number; y: number }
    scale?: number
  }>
  width?: number
  height?: number
  onSpriteClick?: (spriteId: string) => void
}

interface CustomFabricObject extends FabricObject {
  data?: {
    id: string
  }
}

export default function FabricCanvas({ 
  sprites, 
  width = 800, 
  height = 600,
  onSpriteClick 
}: FabricCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const fabricCanvasRef = useRef<Canvas | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const onSpriteClickRef = useRef(onSpriteClick)

  // Keep the callback ref up to date
  useEffect(() => {
    onSpriteClickRef.current = onSpriteClick
  }, [onSpriteClick])

  // Stable event handler that uses the ref
  const handleMouseDown = useCallback((options: TPointerEventInfo<TPointerEvent>) => {
    const target = options.target as CustomFabricObject
    if (target?.data?.id && onSpriteClickRef.current) {
      onSpriteClickRef.current(target.data.id)
    }
  }, [])

  // Initialize the canvas
  useEffect(() => {
    if (canvasRef.current && !fabricCanvasRef.current) {
      const canvas = new Canvas(canvasRef.current, {
        width,
        height,
        backgroundColor: '#f0f0f0',
        selection: true,
        isDrawingMode: false,
        preserveObjectStacking: true,
        enableRetinaScaling: true,
        centeredScaling: true,
        stopContextMenu: true,
        perPixelTargetFind: true,
      })

      // Enable object selection and movement
      canvas.on('mouse:down', handleMouseDown)

      console.log('Canvas initialized:', canvas)
      window.fabricCanvas = canvas

      fabricCanvasRef.current = canvas
      setIsInitialized(true)
    }

    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose()
        fabricCanvasRef.current = null
      }
    }
  }, [width, height, handleMouseDown])

  // Update sprites when they change
  useEffect(() => {
    const canvas = fabricCanvasRef.current
    if (!canvas || !isInitialized) return

    // Get existing sprite IDs
    const existingSprites = new Set(
      canvas.getObjects().map((obj: CustomFabricObject) => obj.data?.id)
    )

    // Only add sprites that don't exist yet
    sprites.forEach(async (sprite) => {
      // Skip if sprite already exists
      if (existingSprites.has(sprite.id)) return

      console.log('Loading new sprite:', sprite)
      try {
        const img = document.createElement('img')
        img.crossOrigin = 'anonymous'
        img.src = sprite.imageUrl

        await new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = reject
        })

        const fabricImage = new FabricImage(img, {
          left: sprite.position?.x ?? Math.random() * (width - 100),
          top: sprite.position?.y ?? Math.random() * (height - 100),
          scaleX: sprite.scale ?? 1,
          scaleY: sprite.scale ?? 1,
          data: { id: sprite.id },
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true,
          cornerStyle: 'circle',
          cornerSize: 12,
          transparentCorners: false,
          lockUniScaling: true,
          centeredRotation: true,
          centeredScaling: true,
          perPixelTargetFind: true,
          targetFindTolerance: 4,
        })

        canvas.add(fabricImage)
        canvas.renderAll()
        console.log('Added new image to canvas:', fabricImage)
      } catch (error) {
        console.error('Error loading sprite:', sprite.id, error)
      }
    })

    // Remove sprites that no longer exist in the props
    const spriteIds = new Set(sprites.map(s => s.id))
    canvas.getObjects().forEach((obj: CustomFabricObject) => {
      if (obj.data?.id && !spriteIds.has(obj.data.id)) {
        canvas.remove(obj)
      }
    })
    canvas.renderAll()
  }, [sprites, width, height, isInitialized])

  return (
    <div className="relative border border-gray-300 rounded-lg overflow-hidden">
      <canvas ref={canvasRef} />
    </div>
  )
} 